import useSWR, { SWRResponse } from "swr";

import { useAuth, useSWRApiErrorHandler } from "../common/hooks";
import { ProjectAccountFormValues } from "../features/collection/components/ProjectAccountForm";
import {
    FormApprover,
    FormApproverDetails,
    ProjectFormValues,
} from "../features/project/components/ProjectForm";
import { StateAndRef, UUID, WorkflowResult } from "../types/common";
import {
    BesuProjectState,
    ProjectState,
    ProjectStateWithNestedStatus,
    ProjectStateWithTxn,
    useProjectListResponse,
} from "../types/project";

import request, { ApiError, requestWithToken } from "./common";
import { PartyResponsePayload } from "./utilities";
import { isUserClaimApprover, isUserPaymentApprover, isUserHSBCOperator } from "../utils/role";
import { UserRole } from "../types/userCurrent";
import { UserStatus } from "../types/users";
import { ApproverDetailsPersonalInfo } from "../types/claim";

const parseToApproverDetails = (
    formApproverDetails: FormApproverDetails,
): ApproverDetailsPersonalInfo => {
    try {
        const approverVal: FormApprover = JSON.parse(
            formApproverDetails.approver,
        );
        return {
            roleName: formApproverDetails.roleName ?? "",
            firstName: approverVal.firstName,
            lastName: approverVal.lastName,
            email: approverVal.email,
            userProfileName: approverVal.userProfileName
        };
    } catch (err) {
        console.error(err);
        return {
            roleName: "",
            firstName: "",
            lastName: "",
            email: "",
            userProfileName: "",
        };
    }
};

const projectApi = {
    create: (
        values: ProjectFormValues,
    ): Promise<ProjectCreationWorkflowResult> => {
        const payload: ProjectCreationRequest = {
            id: values.id,
            name: values.name,
            mainContractor: values.contractor,
            claimApprovers: values.claimApprovers?.map((it) =>
                parseToApproverDetails(it),
            ),
            paymentApprovers: values.paymentOrderApprovers.map((it) =>
                parseToApproverDetails(it),
            ),
        };
        return request<ProjectCreationWorkflowResult, ProjectCreationRequest>(
            "/project/create",
            "POST",
            payload,
        );
    },
    delete: (ref: string): Promise<WorkflowResult<null>> => {
        return request<WorkflowResult<null>>(
            `/project/po/delete/${ref}`,
            "PUT",
        );
    },
    confirmDelete: (ref: string): Promise<WorkflowResult<null>> => {
        return request<WorkflowResult<null>>(
            `/project/delete/${ref}`,
            "DELETE",
        );
    },
    bulkUpdateAccounts: (
        values: ProjectAccountFormValues,
    ): Promise<WorkflowResult<null>> => {
        const payload: BulkUpdateProjectAccountsRequest = {
            idAndBanks: Object.fromEntries(
                Object.entries(values).map(([key, value]) => [
                    key,
                    value.filter((it) => !!it),
                ]),
            ),
        };
        return request<WorkflowResult<null>, BulkUpdateProjectAccountsRequest>(
            "/project/accounts",
            "PATCH",
            payload,
        );
    },
    update: (
        ref: string,
        values: ProjectFormValues,
    ): Promise<ProjectCreationWorkflowResult> => {
        const payload: ProjectCreationRequest = {
            id: values.id,
            name: values.name,
            mainContractor: values.contractor,
            claimApprovers: values.claimApprovers.map((it) =>
                parseToApproverDetails(it),
            ),
            paymentApprovers: values.paymentOrderApprovers.map((it) =>
                parseToApproverDetails(it),
            ),
        };
        return request<ProjectCreationWorkflowResult, ProjectCreationRequest>(
            `/project/update/${ref}`,
            "PUT",
            payload,
        );
    },
    getProjectsPaging: (
        currentRoles: UserRole[],
        page: number = 1,
        size: number = 9,
        projectID: string = "",
    ): Promise<useProjectListResponse> => {
        const params: string[] = [
            `page=${page}`,
            `pageSize=${size}`,
            `projectID=${projectID}`,
        ];
        const projectListUrl = (() => {
            switch (true) {
                case isUserClaimApprover(currentRoles):
                    return `/project/claim-approver/list/all?${params.join("&")}`;
                case isUserPaymentApprover(currentRoles):
                    return `/project/payment-order-approver/list/all?${params.join(
                        "&",
                    )}`;
                case isUserHSBCOperator(currentRoles):
                    return `/project/hsbc-operator/list/all?${params.join("&")}`;
                default:
                    return `/project/list/all?${params.join("&")}`;
            }
        })();
        return request<useProjectListResponse, null>(projectListUrl, "GET", null);
    },
};


export const useProjectList = (
    currentRoles: UserRole[],
    page: number = 1,
    size: number = 9,
    projectID: string = "",
): SWRResponse<useProjectListResponse, ApiError> => {
    const params: string[] = [
        `page=${page}`,
        `pageSize=${size}`,
        `projectID=${projectID}`,
    ];
    const onError = useSWRApiErrorHandler();
    const projectListUrl = (() => {
        switch (true) {
            case isUserClaimApprover(currentRoles):
                return `/project/claim-approver/list/all?${params.join("&")}`;
            case isUserPaymentApprover(currentRoles):
                return `/project/payment-order-approver/list/all?${params.join(
                    "&",
                )}`;
            case isUserHSBCOperator(currentRoles):
                return `/project/hsbc-operator/list/all?${params.join("&")}`;
            default:
                return `/project/list/all?${params.join("&")}`;
        }
    })();
    return useSWR<useProjectListResponse, ApiError>(projectListUrl, request, {
        onError,
    });
};

// export const useProjectList = (
//     currentRoles: UserRole[],
// ): SWRResponse<ProjectStateWithNestedStatus[], ApiError> => {
//     const onError = useSWRApiErrorHandler();
//     const projectListUrl = (() => {
//         switch (true) {
//             case isUserClaimApprover(currentRoles):
//                 return "/project/claim-approver/list/all";
//             case isUserPaymentApprover(currentRoles):
//                 return "/project/payment-order-approver/list/all";
//             default:
//                 return "/project/list/all";
//         }
//     })();
//     return useSWR<ProjectStateWithNestedStatus[], ApiError>(
//         projectListUrl,
//         request,
//         { onError },
//     );
// };

export const useFinanceAllProjectList = (): SWRResponse<
    BesuProjectState[],
    ApiError
> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<BesuProjectState[], ApiError>(
        "/project/finance/list/all",
        request,
        { onError },
    );
};

export const useAllProjectList = (): SWRResponse<
    BesuProjectState[],
    ApiError
> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<BesuProjectState[], ApiError>(
        "/project/no-pagination/list/all",
        request,
        { onError },
    );
};

export const useClaimApproverProjectList = (): SWRResponse<
    ProjectStateWithNestedStatus[],
    ApiError
> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<ProjectStateWithNestedStatus[], ApiError>(
        "/project/claim-approver/list/all",
        request,
        { onError },
    );
};

export const useProjectListWithTxn = (): SWRResponse<
    ProjectStateWithTxn[],
    ApiError
> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<ProjectStateWithTxn[], ApiError>(
        "/project/collections/matched",
        request,
        { onError },
    );
};

export const useProject = (
    linearId: UUID | null,
): SWRResponse<BesuProjectState, ApiError> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<BesuProjectState, ApiError>(
        linearId ? `/project/detail/${linearId}` : linearId,
        request,
        { onError },
    );
};

export const useNewProject = (
    linearId: UUID | null,
): SWRResponse<BesuProjectState, ApiError> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<BesuProjectState, ApiError>(
        linearId ? `/project/detail/${linearId}` : linearId,
        request,
        { onError },
    );
};

export const useProjectWithToken = (
    linearId: UUID | null,
): SWRResponse<BesuProjectState, ApiError> => {
    const { token } = useAuth();
    return useSWR<BesuProjectState, ApiError>(
        token && linearId ? `/token-access/project/detail/${linearId}` : null,
        requestWithToken(token),
    );
};

export const useByUserRoleUserList = (
    userRole: string,
    userProfileId: string,
): SWRResponse<UserResponsePayload[], ApiError> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<UserResponsePayload[], ApiError>(
        `/users/account/role/${userRole}/${userProfileId}`,
        request,
        { onError },
    );
};

export const useImmutableContractor = (
    ref: string,
): SWRResponse<PartyResponsePayload[], ApiError> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<PartyResponsePayload[], ApiError>(
        `/project/getImmutableContractor/${ref}`,
        request,
        { onError },
    );
};

export type UserResponsePayload = {
    email: string;
    enableInvEmailNotif: boolean;
    enableProfileManagement: boolean;
    enableUserManagement: boolean;
    firstName: string;
    id: UUID;
    lastName: string;
    profileName: string;
    profileType: string;
    role: string;
    status: UserStatus;
    accountName: string;
};

type ProjectCreationRequest = {
    id: string;
    name: string;
    mainContractor: string[];
    claimApprovers: ApproverDetailsPersonalInfo[];
    paymentApprovers: ApproverDetailsPersonalInfo[];
};

type BulkUpdateProjectAccountsRequest = {
    idAndBanks: {
        [key: string]: string[];
    };
};

type ProjectCreationWorkflowResult = WorkflowResult<{
    ProjectState: StateAndRef<ProjectState>[];
}>;

export default projectApi;
